import {Timeline, TimelineProps} from "../Timeline/Timeline";
import {
  AcceptedTicketTimelineEvent,
  adjustFromTimeZone,
  DispatchedTicketTimelineEvent,
  DispatchEntity,
  dispatchRequestStatus, dispatchSelector,
  joinSafely,
  RequestedTicketTimelineEvent,
  TicketTimelineEvent,
  TransportAssocEntity,
  TransportEntity,
  transportSelector,
  TransportTicketTimelineEvent,
  useGetDispatchQuery,
  useGetTransportQuery
} from "@ots-frontend/ots-data";
import {TransportTimelineItem} from "./TransportTimelineItem";
import {RequestedTimelineItem} from "./RequestedTimelineItem";
import {AcceptedTimelineItem} from "./AcceptedTimelineItem";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {TFunction} from "i18next";
import {DispatchedTimelineItem} from "./DispatchedTimelineItem";
import {skipToken} from "@reduxjs/toolkit/query";
import {GeofenceEntryTimelineItem} from "../ShiftTimeline/GeofenceEntryTimelineItem";
import {GeofenceExitTimelineItem} from "../ShiftTimeline/GeofenceExitTimelineItem";
import {StartTimelineItem} from "./StartTimelineItem";
import {EndTimelineItem} from "./EndTimelineItem";

const getTimelineItem = (
  t: TFunction<"common"[], undefined, "common"[]>,
  event: TicketTimelineEvent
): React.ReactNode => {
  switch (event.type) {
    case 'TRANSPORT':
      return (
        <Timeline.Item
          key={`transport-${event.id}`}
          title={<TransportTimelineItem.Title event={event}/>}
          bullet={<TransportTimelineItem.Bullet event={event}/>}
        >
          <TransportTimelineItem event={event}/>
        </Timeline.Item>
      );
    case 'REQUESTED':
      return (
        <Timeline.Item
          key={`requested-${event.date.toString()}`}
          title={<RequestedTimelineItem.Title/>}
          bullet={<RequestedTimelineItem.Bullet/>}
        >
          <RequestedTimelineItem event={event}/>
        </Timeline.Item>
      );
    case 'ACCEPTED':
      return (
        <Timeline.Item
          key={`accepted-${event.date.toString()}`}
          title={<AcceptedTimelineItem.Title/>}
          bullet={<AcceptedTimelineItem.Bullet/>}
        >
          <AcceptedTimelineItem event={event}/>
        </Timeline.Item>
      );
    case 'DISPATCHED':
      return (
        <Timeline.Item
          key={`dispatched-${event.date.toString()}`}
          title={<DispatchedTimelineItem.Title/>}
          bullet={<DispatchedTimelineItem.Bullet/>}
        >
          <DispatchedTimelineItem event={event}/>
        </Timeline.Item>
      );
    case "GEOFENCE_ENTRY":
      return (
        <Timeline.Item
          key={`geofence-entry-${event.id}`}
          title={<GeofenceEntryTimelineItem.Title/>}
          bullet={<GeofenceEntryTimelineItem.Bullet event={event}/>}
        >
          <GeofenceEntryTimelineItem event={event}/>
        </Timeline.Item>
      );
    case "GEOFENCE_EXIT":
      return (
        <Timeline.Item
          key={`geofence-exit-${event.id}`}
          title={<GeofenceExitTimelineItem.Title/>}
          bullet={<GeofenceExitTimelineItem.Bullet event={event}/>}
        >
          <GeofenceExitTimelineItem event={event}/>
        </Timeline.Item>
      );
    case "TICKET_START":
      return (
        <Timeline.Item
          key={`start-${event.id}`}
          title={<StartTimelineItem.Title/>}
          bullet={<StartTimelineItem.Bullet event={event}/>}
        >
          <StartTimelineItem event={event}/>
        </Timeline.Item>
      );
    case "TICKET_END":
      return (
        <Timeline.Item
          key={`end-${event.id}`}
          title={<EndTimelineItem.Title/>}
          bullet={<EndTimelineItem.Bullet event={event}/>}
        >
          <EndTimelineItem event={event}/>
        </Timeline.Item>
      );
    default:
      return undefined;
  }
};

const getTimelineEvents = (
  tta: TransportAssocEntity,
  dispatch: DispatchEntity | undefined,
  pickupTransport: TransportEntity | undefined,
  deliveryTransport: TransportEntity | undefined,
): TicketTimelineEvent[] => {
  const events: TicketTimelineEvent[] = [];

  const latestDttm = tta.startDttm ?? tta.createDttm

  if (latestDttm) {
    if (dispatch) {
      const dispatchRequest = dispatch.dispatchRequest
      if (dispatchRequest) {
        const requestedEvent: RequestedTicketTimelineEvent = {
          person: dispatchRequest.createdBy,
          date: dispatchRequest.createDttm ?? latestDttm,
          type: 'REQUESTED',
        };
        events.push(requestedEvent);

        if (dispatchRequest.acceptedDttm || dispatchRequest.status === dispatchRequestStatus.accepted) {
          const event: AcceptedTicketTimelineEvent = {
            person: joinSafely(" ", dispatchRequest.acceptedByFirstName, dispatchRequest.acceptedByLastName),
            date: dispatchRequest.acceptedDttm ?? latestDttm,
            type: 'ACCEPTED',
          };
          events.push(event);
        }
      }
    }

    // dispatch events
    if (tta.dispatchId) {
      const dispatchedEvent: DispatchedTicketTimelineEvent = {
        person: joinSafely(" ", dispatch?.createByName),
        date: dispatch?.createDttm ?? latestDttm,
        type: 'DISPATCHED',
      };
      events.push(dispatchedEvent);
    }
  }

  if (tta.startDttm) {
    events.push({
      id: tta.id,
      date: tta.startDttm,
      type: 'TICKET_START',
    })
  }

  // pickup events
  if (tta.pickupTransportId) {
    const transport = pickupTransport ?? tta.pickupTransport
    if (transport && transport.createDttm) {

      const geofenceEvent = transport.geofenceEvent

      if (geofenceEvent) {
        events.push({
          id: geofenceEvent.id,
          siteId: geofenceEvent.siteId,
          siteName: geofenceEvent.siteName,
          date: geofenceEvent.inDttm,
          type: 'GEOFENCE_ENTRY',
        })
      } else {
        if (transport.inDttm && transport.inDttm <= transport.createDttm) {
          events.push({
            id: transport.id,
            siteId: transport.siteId ?? "-1",
            siteName: transport.siteName,
            date: transport.inDttm,
            type: 'GEOFENCE_ENTRY',
          })
        }
      }

      const event: TransportTicketTimelineEvent = {
        id: transport.id,
        transportType: transport.type,
        quantity: transport.quantityDecimal,
        unitOfMeasure: transport.quantityUnitOfMeasure,
        siteId: transport.siteId,
        siteName: transport.siteName,
        waterTypeName: transport.waterTypeName,
        serviceTypeId: transport.serviceTypeId,
        serviceTypeName: transport.serviceTypeName,
        date: transport.createDttm,
        type: 'TRANSPORT',
      };
      events.push(event);

      if (geofenceEvent && geofenceEvent.outDttm) {
        events.push({
          id: geofenceEvent.id,
          siteId: geofenceEvent.siteId,
          siteName: geofenceEvent.siteName,
          date: geofenceEvent.outDttm,
          type: 'GEOFENCE_EXIT',
        })
      } else {
        if (transport.outDttm && transport.outDttm >= transport.createDttm) {
          events.push({
            id: transport.id,
            siteId: transport.siteId ?? "-1",
            siteName: transport.siteName,
            date: transport.outDttm,
            type: 'GEOFENCE_EXIT',
          })
        }
      }
    }
  }

  // delivery events
  if (tta.deliveryTransportId) {
    const transport = deliveryTransport ?? tta.deliveryTransport
    if (transport && transport.createDttm) {

      const geofenceEvent = transport.geofenceEvent
      if (geofenceEvent) {
        events.push({
          id: geofenceEvent.id,
          siteId: geofenceEvent.siteId,
          siteName: geofenceEvent.siteName,
          date: geofenceEvent.inDttm,
          type: 'GEOFENCE_ENTRY',
        })
      } else {
        if (transport.inDttm && transport.inDttm < transport.createDttm) {
          events.push({
            id: transport.id,
            siteId: transport.siteId ?? "-1",
            siteName: transport.siteName,
            date: transport.inDttm,
            type: 'GEOFENCE_ENTRY',
          })
        }
      }

      const event: TransportTicketTimelineEvent = {
        id: transport.id,
        transportType: transport.type,
        quantity: transport.quantityDecimal,
        unitOfMeasure: transport.quantityUnitOfMeasure,
        siteId: transport.siteId,
        siteName: transport.siteName,
        waterTypeName: transport.waterTypeName,
        serviceTypeId: transport.serviceTypeId,
        serviceTypeName: transport.serviceTypeName,
        date: transport.createDttm,
        type: 'TRANSPORT',
      };
      events.push(event);

      if (geofenceEvent && geofenceEvent.outDttm) {
        events.push({
          id: geofenceEvent.id,
          siteId: geofenceEvent.siteId,
          siteName: geofenceEvent.siteName,
          date: geofenceEvent.outDttm,
          type: 'GEOFENCE_EXIT',
        })
      } else {
        if (transport.outDttm && transport.outDttm > transport.createDttm) {
          events.push({
            id: transport.id,
            siteId: transport.siteId ?? "-1",
            siteName: transport.siteName,
            date: transport.outDttm,
            type: 'GEOFENCE_EXIT',
          })
        }
      }
    }
  }

  if (tta.endDttm) {
    events.push({
      id: tta.id,
      date: tta.endDttm,
      type: 'TICKET_END',
    })
  }

  return events;
};

export type TicketTimelineProps = Omit<TimelineProps, 'children'> & {
  ticket: TransportAssocEntity
}

export const TicketTimeline = (props: TicketTimelineProps) => {
  const {ticket, ...rest} = props

  const {t} = useTranslation(["common"])

  const selectTransport = useMemo(() => {
    return transportSelector()
  }, []);

  const selectDispatch = useMemo(() => {
    return dispatchSelector()
  }, []);

  const pickupTransportArg = useMemo(() => {
    if (ticket.pickupTransportId && !ticket.pickupTransport) {
      return {
        id: ticket.pickupTransportId
      }
    } else {
      return skipToken
    }
  }, [ticket.pickupTransport, ticket.pickupTransportId])

  const deliveryTransportArg = useMemo(() => {
    if (ticket.deliveryTransportId && !ticket.deliveryTransport) {
      return {
        id: ticket.deliveryTransportId
      }
    } else {
      return skipToken
    }
  }, [ticket.deliveryTransport, ticket.deliveryTransportId])

  const {dispatch} = useGetDispatchQuery(ticket.dispatchId ?? skipToken, {
    selectFromResult: (result) => ({
      dispatch: selectDispatch(result)
    })
  });

  const {loadedPickupTransport} = useGetTransportQuery(pickupTransportArg, {
    selectFromResult: (result) => ({
      loadedPickupTransport: selectTransport(result)
    })
  });

  const {loadedDeliveryTransport} = useGetTransportQuery(deliveryTransportArg, {
    selectFromResult: (result) => ({
      loadedDeliveryTransport: selectTransport(result)
    })
  });

  const pickupTransport = loadedPickupTransport ?? ticket.pickupTransport
  const deliveryTransport = loadedDeliveryTransport ?? ticket.deliveryTransport

  const events = useMemo(
    () => getTimelineEvents(ticket, dispatch, pickupTransport, deliveryTransport),
    [ticket, dispatch, pickupTransport, deliveryTransport]
  );

  const nodes = useMemo(() => events.map((e) => getTimelineItem(t, e)), [events, t]);

  return (
    <Timeline {...rest}>
      {nodes.map((n) => {
        return n;
      })}
    </Timeline>
  )
}
